import {usePathway} from "../contexts/PathwayContext";
import Script from "next/script";
import React from "react";

type PathwayScriptProps = {
  shouldWeTrack: boolean
}

const PathwayScript: React.FC<PathwayScriptProps> = ({ shouldWeTrack }) => {
  const { setPathway } = usePathway();

  const environment = process?.env?.NEXT_PUBLIC_ENV ?? 'production';
  const debugMode = ['development', 'staging'].includes(environment);
  const pathwayScriptSrc = `https://pathway.mongodb.com/${debugMode ? 'pathway-debug.js' : ''}`
  const segmentWriteKey = process?.env?.NEXT_PUBLIC_ANALYTICS_SEGMENT_WRITE_KEY;

  const onReady = async (): Promise<void> => {
    try {
      const intervalTimeMs = 500;
      const maxAttempts = 20;
      await waitUntil(() => !!window?.pathway?.default, intervalTimeMs, maxAttempts);
    } catch {
      console.error('Could not load Pathway script');
      return;
    }

    const pathway = window.pathway.default;
    window.segment = pathway;

    pathway.configure({
      debugMode,
      segmentWriteKey
    });

    const profile = pathway.createProfile("customer-portal", [
      'segment', 'cheq', 'onetrust', 'sixsense', 'zoominfo'
    ]);
    profile.load();

    try {
      const intervalTimeMs = 500;
      const maxAttempts = 600;
      await waitUntil(() => profile.isProvidersLoaded(), intervalTimeMs, maxAttempts);
      setPathway(pathway);
    } catch {
      console.error('Could not load Pathway providers');
    }
  };

  if (!shouldWeTrack) {
    return null
  }

  return (
    <Script
      id="pathway-script"
      strategy="afterInteractive"
      onReady={() => {
        onReady();
        return;
      }}
      src={pathwayScriptSrc}
    />
  )
}

const waitUntil = (
  check: () => boolean,
  interval: number,
  maxAttempts: number
): Promise<void> => {
  let attempts = 0;
  return new Promise((resolve, reject) => {
    const timer = setInterval(() => {
      if (check()) {
        clearInterval(timer);
        resolve();
      } else if (++attempts >= maxAttempts) {
        clearInterval(timer);
        reject(new Error('Expired wait'));
      }
    }, interval);
  });
};

export default PathwayScript;